import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
  TextField,
  Box
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useQuery, useMutation } from 'react-query';
import apiClient from '../../request/http-common';

export default function ListWallets() {
  const [wallets, setWallets] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [walletToDelete, setWalletToDelete] = useState(null);
  const [walletToEdit, setWalletToEdit] = useState(null);
  const [editName, setEditName] = useState('');
  const [editImage, setEditImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { isLoading, refetch } = useQuery(
    'crypto-wallets',
    async () => {
      return await apiClient.get('/api/crypto-wallets');
    },
    {
      enabled: false,
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          setWallets(res.data.wallets);
        }
        const event = new Event('processed');
        window.dispatchEvent(event);
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to load wallets');
        const event = new Event('processed');
        window.dispatchEvent(event);
      },
    }
  );

  const { mutate: deleteWallet, isLoading: isDeleting } = useMutation(
    async (id) => {
      return await apiClient.post(`/api/delete-wallet/${id}`);
    },
    {
      onSuccess: (res) => {
        setSuccess(res.data.message);
        setError('');
        setDeleteDialogOpen(false);
        setWalletToDelete(null);
        refetch();
        
        const event = new Event('processed');
        window.dispatchEvent(event);

        setTimeout(() => {
          setSuccess('');
        }, 3000);
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to delete wallet');
        setSuccess('');
        setDeleteDialogOpen(false);
        
        const event = new Event('processed');
        window.dispatchEvent(event);
      },
    }
  );

  const { mutate: updateWallet, isLoading: isUpdating } = useMutation(
    async ({ id, formData }) => {
      return await apiClient.post(`/api/update-wallet/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    {
      onSuccess: (res) => {
        setSuccess(res.data.message);
        setError('');
        setEditDialogOpen(false);
        setWalletToEdit(null);
        setEditName('');
        setEditImage(null);
        setImagePreview('');
        refetch();
        
        const event = new Event('processed');
        window.dispatchEvent(event);

        setTimeout(() => {
          setSuccess('');
        }, 3000);
      },
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to update wallet');
        setSuccess('');
        
        const event = new Event('processed');
        window.dispatchEvent(event);
      },
    }
  );

  useEffect(() => {
    const event = new Event('processing');
    window.dispatchEvent(event);
    refetch();
  }, [refetch]);

  const handleDeleteClick = (wallet) => {
    setWalletToDelete(wallet);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (walletToDelete) {
      const event = new Event('processing');
      window.dispatchEvent(event);
      deleteWallet(walletToDelete.id);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setWalletToDelete(null);
  };

  const handleEditClick = (wallet) => {
    setWalletToEdit(wallet);
    setEditName(wallet.name);
    setImagePreview(wallet.image);
    setEditDialogOpen(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditSubmit = () => {
    if (!editName.trim()) {
      setError('Wallet name is required');
      return;
    }

    const formData = new FormData();
    formData.append('name', editName);
    if (editImage) {
      formData.append('image', editImage);
    }

    const event = new Event('processing');
    window.dispatchEvent(event);
    updateWallet({ id: walletToEdit.id, formData });
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setWalletToEdit(null);
    setEditName('');
    setEditImage(null);
    setImagePreview('');
  };

  return (
    <div style={{ marginTop: '75px' }}>
      <Container>
        <Typography
          mt={4}
          mb={3}
          sx={{ fontWeight: 'bold', fontSize: 24, textAlign: 'center', textTransform: 'uppercase' }}
          variant="h5"
        >
          {isLoading ? 'Loading Wallets...' : 'Crypto Wallets'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper}>
          {wallets.length > 0 && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Image</TableCell>
                  <TableCell>Wallet Name</TableCell>
                  <TableCell>Image URL</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wallets.map((wallet) => (
                  <TableRow key={wallet.id}>
                    <TableCell>{wallet.id}</TableCell>
                    <TableCell>
                      <Avatar
                        src={wallet.image}
                        alt={wallet.name}
                        sx={{ width: 50, height: 50 }}
                      />
                    </TableCell>
                    <TableCell>{wallet.name}</TableCell>
                    <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {wallet.image}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleEditClick(wallet)}
                        disabled={isUpdating}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(wallet)}
                        disabled={isDeleting}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {wallets.length === 0 && !isLoading && (
            <Typography sx={{ p: 3, textAlign: 'center' }}>
              No wallets found
            </Typography>
          )}
        </TableContainer>

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={handleEditCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Edit Wallet</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Wallet Name"
                fullWidth
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                sx={{ mb: 3 }}
                required
              />
              
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                >
                  {editImage ? 'Change Image' : 'Upload New Image (Optional)'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </Button>
              </Box>

              {imagePreview && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Avatar
                    src={imagePreview}
                    alt="Preview"
                    sx={{ width: 100, height: 100 }}
                  />
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleEditCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleEditSubmit} color="success" disabled={isUpdating} variant="contained">
              {isUpdating ? 'Updating...' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
        >
          <DialogTitle>Delete Wallet</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete "{walletToDelete?.name}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
}


